"use client";

import * as React from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { Button, Spinner } from "@/components";
import { cn } from "@/utils";

const REFERRER = "0x618B6Cf82c44C4f2e7997f4498730195259E215b";
const WPLS = "0xA1077a294dDE1B09bB078844df40758a5D0f9a27";
const PHEX = "0x2b591e99afE9f32eAA6214f7B7629768c40Eeb39";
const PULSEX_SWAP =
  "https://pulsex.mypinata.cloud/ipfs/bafybeiajyhfbf6evh4mdabassmbtsy73ci2gmcgh4ffmjkrgsea35vqxba";

const BUY_TABS = {
  pulsex: {
    title: "PulseX",
    subtitle: "PulseChain native DEX",
    src: `${PULSEX_SWAP}/#/?inputCurrency=${WPLS}&outputCurrency=${PHEX}&referrer=${REFERRER}`,
  },
  liberty: {
    title: "Cross chain",
    subtitle: "LibertySwap bridge",
    src: "https://libertyswap.finance",
  },
} as const;

type BuyTab = keyof typeof BUY_TABS;

export function GoPulseX() {
  const [tab, setTab] = React.useState<BuyTab>("pulsex");
  const [isLoading, setIsLoading] = React.useState(true);

  return (
    <Dialog.Root
      onOpenChange={(open) => {
        if (!open) {
          setIsLoading(true);
          setTab("pulsex");
        }
      }}
    >
      <Dialog.Trigger asChild>
        <Button variant="secondary" className="w-full sm:w-auto">
          Buy Hex
        </Button>
      </Dialog.Trigger>
      <Dialog.Portal>
        <Dialog.Overlay className="data-[state=open]:animate-overlayShow fixed inset-0 z-40 bg-black/75 backdrop-blur-md" />
        <Dialog.Content className="data-[state=open]:animate-contentShow fixed left-1/2 top-1/2 z-50 flex max-h-[92vh] w-[min(96vw,42rem)] -translate-x-1/2 -translate-y-1/2 flex-col overflow-hidden rounded-2xl border border-white/10 bg-surface-300 shadow-2xl focus:outline-none">
          <div className="flex items-center justify-between border-b border-white/[0.06] px-5 py-4">
            <Dialog.Title className="text-lg font-semibold text-white">
              Buy HEX
            </Dialog.Title>
            <Dialog.Close asChild>
              <button
                type="button"
                className="rounded-lg p-1.5 text-muted transition-colors hover:bg-surface-400 hover:text-white"
                aria-label="Close"
              >
                <XMarkIcon className="h-5 w-5 stroke-2" />
              </button>
            </Dialog.Close>
          </div>

          <div className="grid grid-cols-2 gap-2 border-b border-white/[0.06] p-3">
            {(Object.keys(BUY_TABS) as BuyTab[]).map((key) => (
              <button
                key={key}
                type="button"
                onClick={() => {
                  setTab(key);
                  setIsLoading(true);
                }}
                className={cn(
                  "rounded-xl border px-3 py-3 text-left transition-colors",
                  tab === key
                    ? "border-hex-400/40 bg-hex-400/10 text-white"
                    : "border-white/10 bg-surface-400/30 text-muted hover:border-white/20 hover:text-white",
                )}
              >
                <p className="text-sm font-semibold">{BUY_TABS[key].title}</p>
                <p className="mt-0.5 text-2xs opacity-80">{BUY_TABS[key].subtitle}</p>
              </button>
            ))}
          </div>

          <div className="relative min-h-[min(68vh,560px)] flex-1 bg-black p-3">
            {isLoading && (
              <div className="absolute inset-3 z-10 grid place-items-center rounded-xl bg-black">
                <Spinner />
              </div>
            )}
            <iframe
              key={tab}
              src={BUY_TABS[tab].src}
              title={BUY_TABS[tab].title}
              allow="clipboard-read; clipboard-write; wallet; payment"
              className="h-[min(68vh,560px)] w-full rounded-xl border-0"
              onLoad={() => setIsLoading(false)}
            />
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

export default GoPulseX;
